import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { getSortedObjectCollection } from "sprint-common-web";

import { ContentVersionDetails } from "../../../../common/components/content-version/ContentVersion";
import { useAssociateContentFormContext } from "../context/useAssociateContentFormContext";
import { ContentAssociationMovieVersionDetails, ensureMovieAssociateDetails } from "../types/ContentAssociationDetails";
import { ContentAssociationFormState } from "../types/ContentAssociationFormState";

interface ContentAssociationMovieSelectionOpts {
    versions: ContentAssociationMovieVersionDetails[];
}

export const useContentAssociationMovieForm = () => {
    const { contentDetails } = useAssociateContentFormContext();
    const { getValues, setValue } = useFormContext<ContentAssociationFormState>();

    const [opts, setOpts] = useState<ContentAssociationMovieSelectionOpts>({
        versions: []
    });

    const init = () => {
        if (!contentDetails) return;

        ensureMovieAssociateDetails(contentDetails);

        setOpts({
            versions: sortVersionsByType(filterVersions(contentDetails?.versions, getValues("versionIds")))
        });
    };

    const addNewVersion = (newVersion: ContentVersionDetails) => {
        setOpts((prevOpts) => ({
            versions: sortVersionsByType([newVersion, ...prevOpts.versions])
        }));

        const currentIds = getValues("versionIds") ?? [];

        if (shouldSelectNewVersion(currentIds, newVersion)) {
            setValue("versionIds", [...currentIds, newVersion.id]);
        }
    };

    const shouldSelectNewVersion = (selectedVersionIds: string[], newVersion: ContentVersionDetails) => {
        const selectedVersion = opts.versions.find((version) => selectedVersionIds.includes(version.id));

        if (!selectedVersion) return true;

        return (
            newVersion.type.id === selectedVersion.type.id &&
            newVersion.brandings?.length === selectedVersion.brandings?.length
        );
    };

    return {
        init,

        opts,
        setOpts,

        addNewVersion
    };
};

const filterVersions = (
    versions: ContentAssociationMovieVersionDetails[],
    selectedVersionIds?: string[]
): ContentAssociationMovieVersionDetails[] => {
    const filtered = versions.filter(
        (version) => !isMergedVersion(version) && isAllowedByArchiveRule(version, selectedVersionIds)
    );

    return getSortedObjectCollection(filtered, "asc", (listItem) => {
        return listItem.type.name;
    });
};

const sortVersionsByType = (arr: ContentAssociationMovieVersionDetails[]) =>
    arr.slice().sort((a, b) => a.type.id.localeCompare(b.type.id));

const isMergedVersion = (version: ContentAssociationMovieVersionDetails): boolean => !!version.mergedTargetId;

const isAllowedByArchiveRule = (
    version: ContentAssociationMovieVersionDetails,
    selectedVersionIds?: string[]
): boolean => !isArchivedVersion(version) || !!selectedVersionIds?.includes(version.id);

const isArchivedVersion = (version: ContentAssociationMovieVersionDetails): boolean =>
    !!version.buzzVersion?.isArchived;
